import { useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { useSend } from './SendContext';
import { useResponseLength } from '../dashboard/ResponseLengthContext';
import { useComposerStaging } from './ComposerStagingContext';
import { useAudioReply } from './AudioReplyContext';
import { useMe } from '../auth/MeContext';
import { useT } from '../I18nContext';
import { AttachButton } from './AttachButton';
import { MicButton } from './MicButton';
import { AudioReplyToggle } from './AudioReplyToggle';
import { AttachmentStaging } from './AttachmentStaging';

/**
 * Bottom-of-the-page composer. Auto-grow textarea (1 → 10 lines),
 * Enter to send, Shift+Enter for newline. Refocuses after send so
 * the user can keep typing without reaching for the mouse.
 *
 * Surrounded by:
 *   - AttachmentStaging      shows pending attachment (if any)
 *   - AttachButton (paperclip)
 *   - MicButton (voice → STT → fills textarea)
 *   - Send button
 *   - AudioReplyToggle in the footer line
 *
 * Disabled while:
 *   - the send mutation is in flight
 *   - an attachment is mid-upload (so we don't send `attachment_ids`
 *     before the upload completes)
 */
export function Composer() {
  const [text, setText] = useState('');
  // Interim transcript while a streaming STT session is in flight.
  // Held alongside `text` so the user's pre-mic input is the canonical
  // state and partials can be cleared without touching it. Rendered
  // in the textarea as `text + ' ' + partial`; on the final, the
  // mic handler clears the partial AND fires handleVoiceTranscript
  // (which sends the final). Empty string clears the partial.
  const [partial, setPartial] = useState('');
  const [micError, setMicError] = useState<string | null>(null);
  const { ask, isPending, errorMessage, clearError } = useSend();
  const { length } = useResponseLength();
  const { staged, status: stagingStatus, clear: clearStaging } =
    useComposerStaging();
  const { enabled: audioRepliesEnabled } = useAudioReply();
  const { me } = useMe();
  const t = useT();
  // STT provider for the active tenant. Drives whether MicButton
  // opens a streaming WebSocket; the resolution chain (client > global
  // > 'whisper') happens server-side in /api/platform/me.
  const sttProvider = me?.stt_provider ?? 'whisper';
  // VAD silence threshold. Server-resolved by the same client > global
  // > 1200ms chain. /me returns the effective value (never 0), and
  // MicButton clamps to [300, 5000] defensively.
  const silenceDurationMs = me?.voice_vad_silence_ms;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Composite value the textarea displays: user-typed prefix + the
  // latest streaming partial (when one is in flight). Separator only
  // when both pieces are non-empty. Submit / send paths always read
  // from `text` directly so a partial that hasn't finalised yet
  // doesn't accidentally get sent.
  const displayText = partial
    ? (text ? text + ' ' + partial : partial)
    : text;

  // Auto-grow up to ~10 lines, then scroll inside. Re-fits when
  // partials arrive too, so a long streaming transcript expands the
  // textarea the same way typed text would.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 240) + 'px';
  }, [displayText]);

  // Refocus after a send completes (mutation went pending → not
  // pending). Lets the user fire questions without the mouse.
  const wasPending = useRef(false);
  useEffect(() => {
    if (wasPending.current && !isPending) {
      textareaRef.current?.focus();
    }
    wasPending.current = isPending;
  }, [isPending]);

  const isUploading = stagingStatus === 'uploading';
  const sendDisabled =
    isPending || isUploading || (!text.trim() && !staged);

  function handleSend() {
    if (sendDisabled) return;
    const trimmed = text.trim();
    const attachmentIds = staged ? [staged.meta.id] : undefined;
    setText('');
    clearStaging();
    setMicError(null);
    ask(
      // The legacy server tolerates an empty message when an
      // attachment is present ("describe this image"). Fall back
      // to a single space to keep the optimistic user bubble
      // from looking literally empty.
      trimmed || ' ',
      length,
      {
        attachmentIds,
        audioRequested: audioRepliesEnabled,
      },
    );
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // Voice input bypasses the textarea entirely. The mic button
  // auto-stops on silence and we send the transcript straight
  // through, with audio_requested forced ON so the assistant
  // replies in audio too. That makes voice-in → voice-out feel
  // like a single gesture, the way the major voice assistants
  // work - instead of:
  //   tap mic → speak → tap stop → see transcript → tap send
  //   → glance at the tiny audio-replies toggle that nobody
  //   notices → wait for text reply
  // it's now:
  //   tap mic → speak → (silence) → answer is spoken back.
  //
  // Any text already in the textarea is left alone - we don't
  // splice voice into mid-typed text. If the user wants to mix,
  // they can type, send, then mic.
  function handleVoiceTranscript(transcript: string) {
    const trimmed = transcript.trim();
    // Always clear any in-flight partial: even an empty final means
    // the stream resolved and we don't want a stale partial lingering
    // in the textarea.
    setPartial('');
    if (!trimmed) return;
    const attachmentIds = staged ? [staged.meta.id] : undefined;
    clearStaging();
    setMicError(null);
    ask(trimmed, length, {
      attachmentIds,
      // Force-enable for this single send regardless of the
      // composer-footer toggle. Voice users get voice replies
      // by default; the toggle is for typed users who want to
      // opt in to spoken answers.
      audioRequested: true,
    });
  }

  return (
    <div className="composer">
      {errorMessage && (
        <div
          className="auth-alert auth-alert-danger composer-error"
          role="alert"
        >
          <span>{errorMessage}</span>
          <button
            type="button"
            className="composer-error-dismiss"
            onClick={clearError}
            aria-label={t('composer.dismiss_error')}
          >
            ×
          </button>
        </div>
      )}

      {micError && (
        <div className="auth-alert auth-alert-warning composer-error" role="alert">
          <span>{micError}</span>
          <button
            type="button"
            className="composer-error-dismiss"
            onClick={() => setMicError(null)}
            aria-label={t('composer.dismiss')}
          >
            ×
          </button>
        </div>
      )}

      <AttachmentStaging />

      <div className="composer-box">
        <AttachButton />
        <MicButton
          onTranscript={handleVoiceTranscript}
          onError={setMicError}
          disabled={isPending}
          sttProvider={sttProvider}
          onPartial={setPartial}
          silenceDurationMs={silenceDurationMs}
        />
        <textarea
          ref={textareaRef}
          className={
            'composer-input' + (partial ? ' composer-input-partial' : '')
          }
          placeholder={t('composer.placeholder')}
          // displayText concatenates the user's typed prefix with the
          // streaming partial; on submit / send we read from `text`
          // directly so the in-flight partial isn't accidentally sent.
          value={displayText}
          // While a streaming partial is in flight the textarea is
          // visually showing concatenated content; editing that would
          // collide with the next partial. The user can edit again
          // once recording/streaming completes and partial clears.
          readOnly={!!partial}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isPending}
          rows={1}
          aria-label={t('composer.placeholder')}
        />
        <button
          type="button"
          className="composer-send"
          onClick={handleSend}
          disabled={sendDisabled}
          aria-label={t('composer.send')}
          title={t('composer.send_title')}
        >
          <Send size={16} />
        </button>
      </div>

      <div className="composer-footer-row">
        <p className="composer-meta">
          Answers are drawn from your workspace documents. Always verify
          critical steps.
        </p>
        <AudioReplyToggle />
      </div>
    </div>
  );
}
